import { useMemo } from "react";
import { useGetDirectorOrgMetricsQuery } from "graphql/__generated__/hooks";
import { OrgCountBarChartItem } from "components/Charts/OrgCountBarChart";
import { isTestOrganizationName, excludeTestOrganizationVariables } from "utils/testOrganization";

export type DirectorOrgMetricRow = {
  orgId: string;
  orgName: string;
  learners: number;
  learnersAcceptedTerms: number;
  coaches: number;
  coachesAcceptedTerms: number;
  feedbacks: number;
};

function countByOrgId(
  rows: Array<{ org_id?: string | null; accepted_terms_of_use?: boolean }>,
  predicate?: (row: {
    org_id?: string | null;
    accepted_terms_of_use?: boolean;
  }) => boolean,
): Record<string, number> {
  return rows.reduce(
    (acc, row) => {
      if (!row.org_id || (predicate && !predicate(row))) return acc;
      acc[row.org_id] = (acc[row.org_id] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );
}

export function useDirectorOrgMetrics(orgIds: string[], sinceDate: string) {
  const { data, loading, error } = useGetDirectorOrgMetricsQuery({
    variables: { orgIds, sinceDate, ...excludeTestOrganizationVariables },
    skip: orgIds.length === 0,
  });

  const rows = useMemo<DirectorOrgMetricRow[]>(() => {
    const organizations = data?.organization || [];
    const learnerRows = data?.learners || [];
    const coachRows = (data?.coaches || []).map((coach) => ({
      org_id: coach.org_id,
      accepted_terms_of_use: coach.user?.accepted_terms_of_use,
    }));
    const learnerCounts = countByOrgId(learnerRows);
    const learnersAcceptedCounts = countByOrgId(learnerRows, (learner) =>
      Boolean(learner.accepted_terms_of_use),
    );
    const coachCounts = countByOrgId(coachRows);
    const coachesAcceptedCounts = countByOrgId(coachRows, (coach) =>
      Boolean(coach.accepted_terms_of_use),
    );
    const feedbackCounts = countByOrgId(data?.feedback || []);

    return organizations.map((organization) => ({
      orgId: organization.org_id,
      orgName: organization.name,
      learners: learnerCounts[organization.org_id] ?? 0,
      learnersAcceptedTerms:
        learnersAcceptedCounts[organization.org_id] ?? 0,
      coaches: coachCounts[organization.org_id] ?? 0,
      coachesAcceptedTerms:
        coachesAcceptedCounts[organization.org_id] ?? 0,
      feedbacks: feedbackCounts[organization.org_id] ?? 0,
    }));
  }, [data]);

  const learnersChartData = useMemo<OrgCountBarChartItem[]>(
    () =>
      rows.map((row) => ({
        org: row.orgName,
        count: row.learners,
        learners: row.learners,
        acceptedTerms: row.learnersAcceptedTerms,
      })),
    [rows],
  );

  const coachesChartData = useMemo<OrgCountBarChartItem[]>(
    () =>
      rows.map((row) => ({
        org: row.orgName,
        count: row.coaches,
        coaches: row.coaches,
        acceptedTerms: row.coachesAcceptedTerms,
      })),
    [rows],
  );

  const feedbacksChartData = useMemo<OrgCountBarChartItem[]>(
    () =>
      rows
        .filter((row) => !isTestOrganizationName(row.orgName))
        .map((row) => ({
          org: row.orgName,
          count: row.feedbacks,
        })),
    [rows],
  );

  return {
    rows,
    learnersChartData,
    coachesChartData,
    feedbacksChartData,
    loading,
    error,
  };
}
